import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { variation } from 'ember-launch-darkly';
import { not } from 'ember-truth-helpers';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsLinkInline,
  HdsDropdown,
  HdsBadge,
} from '@hashicorp/design-system-components/components';
import { CoreLayoutMain, CoreCardSummary } from 'core/components';
import { optionForSourceState } from 'core/helpers';
import BillingCard from 'hcp/components/page/organizations/detail/billing-card';
import UserPrincipalsCard from 'hcp/components/page/organizations/detail/user-principals-card';
import ProjectsCard from 'hcp/components/page/organizations/detail/projects-card';

/**
 * The billing context for the current billing account.
 * @argument billing
 * @type {Object}
 */

/**
 * The running statement for the current billing account.
 * @argument runningStatement
 * @type {Object}
 */

/**
 * The organization to render details for.
 * @argument organization
 * @type {Object}
 */

/**
 * The projects associated with the organization.
 * @argument projects
 * @type {Array}
 */

/**
 * The user principals associated with the organization.
 * @argument userPrincipals
 * @type {Array}
 */

/**
 * A boolean flag that indicates that a user has no organization permissions
 * @argument hasNoOrgRole
 * @type {Boolean}
 */
/**
 *
 * `PageOrganizationsDetailOverview` displays general information about an
 * organization's billing, projects, and user principals.
 *
 *
 * ```
 * <Page::Organizations::Detail::Overview
 *   @billing={{this.model.billing}}
 *   @runningStatement={{this.model.runningStatement}}
 *   @organization={{this.model.organization}}
 *   @projects={{this.model.projects}}
 *   @userPrincipals={{this.model.userPrincipals}}
 *   @hasNoOrgRole={{this.model.hasNoOrgRole}}
 * @
 * />
 * ```
 *
 * @class OrganizationsDetailOverview
 *
 */
export default class PageOrganizationsDetailOverview extends Component {
  @service('resources.organization') organizations;
  @service userContext;
  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          {{#if (variation "hcp-ui-left-side-nav-new-structure")}}
            <PH.Title data-test-organization-overview-title>
              {{t "nav.name-dashboard" name=(t "nav.organization.header")}}
            </PH.Title>
          {{else}}
            <PH.Title data-test-organization-overview-title>
              {{@organization.name}}
            </PH.Title>
          {{/if}}
          <PH.IconTile @icon="dashboard" />
          {{#if (not (variation "hcp-ui-left-side-nav-new-structure"))}}
            <PH.Subtitle data-test-organization-overview-subtitle>
              {{t "nav.name-dashboard" name=(t "nav.organization.header")}}
            </PH.Subtitle>
          {{/if}}
          <PH.Description>
            <span data-test-organization-overview-description>
              {{t
                "components.page.organizations.detail.overview.overview-of-an-organization"
              }}
            </span>
            <HdsLinkInline
              class="hds-typography-body-200"
              @color="secondary"
              @href="https://developer.hashicorp.com/hcp/docs/hcp/admin/orgs"
              @icon="external-link"
              data-test-organization-overview-learn-more
            >
              {{t "components.page.organizations.detail.overview.learn-more"}}
            </HdsLinkInline>
          </PH.Description>
          {{#unless @hasNoOrgRole}}
            <PH.Actions>
              <HdsDropdown
                @listPosition="bottom-right"
                data-test-organization-overview-manage-dropdown
                as |dd|
              >
                <dd.ToggleButton
                  @color="primary"
                  @text={{t
                    "components.page.organizations.detail.overview.manage-organization"
                  }}
                  data-test-organization-overview-manage-toggle
                />
                {{#each this.organizations.links as |link|}}
                  <dd.Interactive
                    @icon={{link.icon}}
                    @models={{link.models}}
                    @query={{link.query}}
                    @route={{link.route}}
                    @text={{link.text}}
                    data-test-organization-overview-manage-link={{link.id}}
                  />
                {{/each}}
              </HdsDropdown>
            </PH.Actions>
          {{/unless}}
        </HdsPageHeader>
      </:header>
      <:alert>
        <CoreCardSummary
          @type="horizontal"
          data-test-organization-overview-summary-card
          as |Card|
        >
          <Card.Pair as |P|>
            <P.Key
              data-test-organization-overview-summary-card-key
              data-test-organization-overview-summary-card-key-status
            >{{t
                "components.page.organizations.detail.overview.organization-status"
              }}</P.Key>
            <P.Value
              data-test-organization-overview-summary-card-value
              data-test-organization-overview-summary-card-value-status
            >
              {{#let
                (optionForSourceState "ORGANIZATION" @organization.state)
                as |option|
              }}
                <HdsBadge
                  @color={{option.color}}
                  @icon="check"
                  @isIconOnly={{option.isIconOnly}}
                  @size="small"
                  @text={{t option.text}}
                  @type={{option.type}}
                />
              {{/let}}
            </P.Value>
          </Card.Pair>
          {{#unless @hasNoOrgRole}}
            <Card.Pair as |P|>
              <P.Key
                data-test-organization-overview-summary-card-key
                data-test-organization-overview-summary-card-key-sso-enabled
              >{{t "components.page.organizations.detail.overview.sso"}}</P.Key>
              <P.Value
                data-test-organization-overview-summary-card-value
                data-test-organization-overview-summary-card-value-sso-enabled
              >
                {{#if this.userContext.organizationMeta.ssoEnabled}}
                  {{t
                    "components.page.organizations.detail.overview.sso-enabled"
                  }}
                {{else}}
                  {{t
                    "components.page.organizations.detail.overview.sso-disabled"
                  }}
                {{/if}}
              </P.Value>
            </Card.Pair>
            <Card.LinkStandalone
              @icon="arrow-right"
              @iconPosition="trailing"
              @route="cloud.orgs.detail.settings"
              @text={{t
                "components.page.organizations.detail.overview.view-organization-settings"
              }}
              data-test-organization-overview-summary-card-standalone-view-organization-settings
            />
          {{/unless}}
        </CoreCardSummary>
      </:alert>
      <:body>
        <div
          class="organization-detail-overview"
          data-test-organization-overview-container
        >
          <div class="organization-detail-overview-card-grid">
            {{#if (can "view billing-account")}}
              <BillingCard
                @billing={{this.userContext.billing}}
                @organization={{@organization}}
                @projects={{@projects}}
                @runningStatement={{@runningStatement}}
                data-test-organization-overview-billing-card
              />
            {{/if}}
            {{#unless @hasNoOrgRole}}
              <UserPrincipalsCard
                @organization={{@organization}}
                data-test-organization-overview-user-principals-card
              />
            {{/unless}}
            {{#if @projects}}
              <ProjectsCard
                @organization={{@organization}}
                @projectCount={{@projects.length}}
                data-test-organization-overview-projects-card
              />
            {{/if}}
          </div>
        </div>
      </:body>
    </CoreLayoutMain>
  </template>
}
